import { z } from "zod/v4";
import type { IsoWeekday as DatesIsoWeekday } from "@visioni/a3s-dates";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { FEATURE_NAME } from "../constants";
import { TypePath } from "./report-types";

/**
 * ISO weekday: Monday = 1 … Sunday = 7. The type is `@visioni/a3s-dates`'s,
 * the one source of truth; the schema is checked against it.
 */
export const IsoWeekday = z.literal([1, 2, 3, 4, 5, 6, 7]) satisfies z.ZodType<DatesIsoWeekday>;
export type IsoWeekday = DatesIsoWeekday;

/** Emailed after every daily run. A weekday is rejected, not ignored. */
const DailyFields = { frequency: z.literal("daily"), weekday: z.never().optional() };
/** Emailed once a week, on `weekday` (or the next run after it, if that run didn't email). */
const WeeklyFields = { frequency: z.literal("weekly"), weekday: IsoWeekday };

/**
 * How often a site's **report email delivery** sends: daily, or weekly on a
 * chosen weekday. One discriminated union, so a weekly delivery without a
 * weekday, or a daily one with one, cannot decode.
 */
export const ReportEmailDeliveryFrequency = z.discriminatedUnion("frequency", [
  z.object(DailyFields),
  z.object(WeeklyFields),
]);
export type ReportEmailDeliveryFrequency = z.output<typeof ReportEmailDeliveryFrequency>;

/** The caller's report email delivery for one site: off, or on at a frequency. */
export const ReportEmailDeliveryState = z.union([
  z.object({ enabled: z.literal(false) }),
  z.object({ enabled: z.literal(true), ...DailyFields }),
  z.object({ enabled: z.literal(true), ...WeeklyFields }),
]);
export type ReportEmailDeliveryState = z.output<typeof ReportEmailDeliveryState>;

/** A PUT body is just the frequency. */
export const PutReportEmailDeliveryBody = ReportEmailDeliveryFrequency;
export type PutReportEmailDeliveryBody = z.output<typeof PutReportEmailDeliveryBody>;

/**
 * Path schema for `{type}/sites/{siteId}/...`. `siteId` is decoded with the
 * `SiteId` codec, so a malformed id is rejected before the handler runs.
 */
export const SiteEmailDeliveryPath = TypePath.extend({ siteId: SiteIdCodec });
export type SiteEmailDeliveryPath = z.output<typeof SiteEmailDeliveryPath>;

/**
 * `{type}/sites/{siteId}/email-delivery` — the calling user's own report email
 * delivery for one site. Each site is configured separately.
 *
 * There is no user id in the path or body: every method acts on the
 * authenticated caller's row only, so no one can read or change another
 * user's delivery.
 */
export const reportEmailDeliveryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/sites/{siteId}/email-delivery",
  resourcePathSchema: SiteEmailDeliveryPath,
  scope: "tenant",
});

export const getReportEmailDeliveryEndpoint = defineEndpoint(reportEmailDeliveryResource, {
  responseSchema: ReportEmailDeliveryState,
});

/** Creates or replaces the caller's delivery for the site and returns the same shape as GET. */
export const putReportEmailDeliveryEndpoint = defineEndpoint(reportEmailDeliveryResource, {
  method: "PUT",
  bodySchema: PutReportEmailDeliveryBody,
  responseSchema: ReportEmailDeliveryState,
});

/** Stops the caller's delivery for the site. Answers `204 No Content`, whether or not one existed. */
export const deleteReportEmailDeliveryEndpoint = defineEndpoint(reportEmailDeliveryResource, {
  method: "DELETE",
  responseSchema: z.undefined(),
});
